import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react-refresh/only-export-components */
import { AlertTriangle, Info, Trash2, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState, } from 'react';
import { Button } from './Button';
import { cn } from '@/lib/utils';
const Ctx = createContext(null);
export function ConfirmProvider({ children }) {
    const [active, setActive] = useState(null);
    const confirm = useCallback((opts) => {
        return new Promise((resolve) => {
            setActive({ ...opts, resolve });
        });
    }, []);
    const finish = useCallback((ok) => {
        if (active)
            active.resolve(ok);
        setActive(null);
    }, [active]);
    // Escape to cancel
    useEffect(() => {
        if (!active)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                finish(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [active, finish]);
    return (_jsxs(Ctx.Provider, { value: { confirm }, children: [children, active && _jsx(ConfirmModal, { modal: active, onFinish: finish })] }));
}
export function useConfirm() {
    const ctx = useContext(Ctx);
    if (ctx === null)
        throw new Error('useConfirm must be used within ConfirmProvider');
    return ctx;
}
function ConfirmModal({ modal, onFinish, }) {
    // First focusable element in the dialog auto-receives focus via the browser.
    const tone = modal.tone ?? 'default';
    const Icon = tone === 'danger' ? AlertTriangle : tone === 'info' ? Info : Trash2;
    const iconColor = tone === 'danger' ? 'bg-red-500/10 text-red-600' :
        tone === 'info' ? 'bg-blue-500/10 text-blue-600' :
            'bg-amber-500/10 text-amber-600';
    return (_jsxs("div", { role: "dialog", "aria-modal": "true", "aria-labelledby": "confirm-title", className: "fixed inset-0 z-[110] flex items-end justify-center p-4 sm:items-center", children: [_jsx("button", { type: "button", onClick: () => onFinish(false), "aria-label": "Dismiss", className: "fixed inset-0 bg-foreground/50 backdrop-blur-sm" }), _jsxs("div", { className: cn('relative z-[1] w-full max-w-md rounded-xl border bg-card p-5 shadow-xl', 'animate-in fade-in zoom-in-95 duration-150'), children: [_jsx("button", { type: "button", onClick: () => onFinish(false), className: "absolute right-3 top-3 rounded p-1 text-muted-foreground hover:bg-muted", "aria-label": "Close", children: _jsx(X, { className: "h-4 w-4" }) }), _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("span", { className: cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', iconColor), children: _jsx(Icon, { className: "h-5 w-5" }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("h2", { id: "confirm-title", className: "text-base font-semibold", children: modal.title }), modal.description && (_jsx("div", { className: "mt-1 text-sm text-muted-foreground", children: modal.description }))] })] }), _jsxs("div", { className: "mt-5 flex justify-end gap-2", children: [_jsx(Button, { variant: "ghost", onClick: () => onFinish(false), children: modal.cancelLabel ?? 'Cancel' }), _jsx(Button, { variant: tone === 'danger' ? 'destructive' : 'primary', onClick: () => onFinish(true), children: modal.confirmLabel ?? 'Confirm' })] })] })] }));
}
